import React, { useState, useEffect } from 'react';
import { bountyService } from '../../services/bountyService';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon,
  XMarkIcon,
  CheckIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';
import LogoLoader from "../Common/LogoLoader";

const BountyManagement = () => {
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBounty, setEditingBounty] = useState(null);
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageCompressing, setImageCompressing] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    type: 'sale',
    requirement: {
      value: '',
      unit: 'kg',
    },
    reward: {
      type: 'cash',
      value: '',
      description: '',
    },
    isActive: true,
  });

  useEffect(() => {
    loadBounties();
  }, []);

  const loadBounties = async () => {
    try {
      setLoading(true);
      const response = await bountyService.getAllBounties();
      const bountiesData = response.data?.data || response.data || [];
      setBounties(Array.isArray(bountiesData) ? bountiesData : []);
    } catch (error) {
      console.error('Error loading bounties:', error);
      setMessage('Error loading bounties');
      setBounties([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('Image size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage('Please select a valid image file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setImageCompressing(true);
        // Compress image if it's too large
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 800px width/height)
          const maxSize = 800;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          
          setImagePreview(compressedDataUrl);
          setFormData(prev => ({
            ...prev,
            image: compressedDataUrl,
          }));
          setImageCompressing(false);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setMessage('');
      
      // Check if image is too large
      if (formData.image && formData.image.length > 10 * 1024 * 1024) { // 10MB limit
        setMessage('Image is too large. Please compress it or choose a smaller image.');
        return;
      }
      
      if (editingBounty) {
        await bountyService.updateBounty(editingBounty._id, formData);
        setMessage('Bounty updated successfully!');
      } else {
        await bountyService.createBounty(formData);
        setMessage('Bounty created successfully!');
      }
      
      setShowModal(false);
      setEditingBounty(null);
      resetForm();
      loadBounties();
    } catch (error) {
      console.error('Error saving bounty:', error);
      
      if (error.response?.status === 413) {
        setMessage('Image is too large. Please compress it or choose a smaller image.');
      } else if (error.response?.data?.message) {
        setMessage(error.response.data.message);
      } else {
        setMessage('Error saving bounty. Please try again.');
      }
    }
  };

  const handleEdit = (bounty) => {
    setEditingBounty(bounty);
    setFormData({
      title: bounty.title || '',
      description: bounty.description || '',
      image: bounty.image || '',
      type: bounty.type || 'sale',
      requirement: {
        value: bounty.requirement?.value || '',
        unit: bounty.requirement?.unit || 'kg',
      },
      reward: {
        type: bounty.reward?.type || 'cash',
        value: bounty.reward?.value || '',
        description: bounty.reward?.description || '',
      },
      isActive: bounty.isActive !== undefined ? bounty.isActive : true,
    });
    setImagePreview(bounty.image || null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bounty?')) {
      try {
        await bountyService.deleteBounty(id);
        setMessage('Bounty deleted successfully!');
        loadBounties();
      } catch (error) {
        console.error('Error deleting bounty:', error);
        setMessage('Error deleting bounty');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image: '',
      type: 'sale',
      requirement: {
        value: '',
        unit: 'kg',
      },
      reward: {
        type: 'cash',
        value: '',
        description: '',
      },
      isActive: true,
    });
    setImagePreview(null);
  };

  const openModal = () => {
    setEditingBounty(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBounty(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bounty Management</h1>
          <p className="text-gray-600">Manage rewards and incentives for users</p>
        </div>
        <button
          onClick={openModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Bounty
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes('successfully')
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message}
        </div>
      )}

      {/* Bounties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {bounties.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <GiftIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bounties found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new bounty.</p>
          </div>
        ) : (
          bounties.map((bounty) => (
          <div key={bounty._id} className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Image */}
            <div className="h-48 bg-gray-200 flex items-center justify-center">
              {bounty.image ? (
                <img
                  src={bounty.image}
                  alt={bounty.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <PhotoIcon className="h-12 w-12 text-gray-400" />
              )}
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{bounty.title}</h3>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    bounty.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {bounty.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <p className="text-gray-600 text-sm mb-3">{bounty.description}</p>

              {/* Requirement */}
              <div className="mb-3">
                <span className="text-sm font-medium text-gray-700">Requirement: </span>
                <span className="text-sm text-gray-600">
                  {bounty.requirement.value} {bounty.requirement.unit}
                </span>
              </div>

              {/* Reward */}
              <div className="mb-4">
                <span className="text-sm font-medium text-gray-700">Reward: </span>
                <span className="text-sm text-gray-600">{bounty.reward.value}</span>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(bounty)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(bounty._id)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingBounty ? 'Edit Bounty' : 'Add New Bounty'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image
                </label>
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                    ) : (
                      <PhotoIcon className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="bounty-image-upload"
                      disabled={imageCompressing}
                    />
                    <label
                      htmlFor="bounty-image-upload"
                      className={`cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${
                        imageCompressing ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {imageCompressing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          Compressing...
                        </>
                      ) : (
                        <>
                          <PhotoIcon className="h-4 w-4 mr-2" />
                          Upload Image
                        </>
                      )}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      JPG, PNG or GIF (max 5MB) - Images will be automatically compressed
                    </p>
                  </div>
                </div>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="sale">Sale</option>
                  <option value="referral">Referral</option>
                </select>
              </div>

              {/* Requirement */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Requirement Value
                  </label>
                  <input
                    type="number"
                    name="requirement.value"
                    value={formData.requirement.value}
                    onChange={handleInputChange}
                    required
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit
                  </label>
                  <select
                    name="requirement.unit"
                    value={formData.requirement.unit}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="kg">Kilograms</option>
                    <option value="people">People</option>
                    <option value="referrals">Referrals</option>
                  </select>
                </div>
              </div>

              {/* Reward Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Type
                </label>
                <select
                  name="reward.type"
                  value={formData.reward.type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="cash">Cash</option>
                  <option value="product">Product</option>
                  <option value="service">Service</option>
                </select>
              </div>

              {/* Reward Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Value
                </label>
                <input
                  type="text"
                  name="reward.value"
                  value={formData.reward.value}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., ₦10,000 or Free DustBin"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Reward Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Description
                </label>
                <textarea
                  name="reward.description"
                  value={formData.reward.description}
                  onChange={handleInputChange}
                  required
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {editingBounty ? 'Update' : 'Create'} Bounty
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BountyManagement;
